import { useSyncExternalStore } from "react";

/**
 * Persisted "license blocked" marker for the app itself.
 *
 * The service worker only enforces its lockdown while offline (it must let
 * online navigations through so the app can lift a block). A device that is
 * "online" without reaching our server (Wi-Fi without internet, captive
 * portal, blocked API host) would then boot the app with a cached token that
 * still looks valid locally — and work for days. So the app mirrors the
 * block itself:
 *
 *   - `markLicenseBlocked()` / `markLicenseUnblocked()` are written alongside
 *     the SW `LICENSE_BLOCKED` / `LICENSE_UNBLOCKED` messages
 *     (`useSwLicenseBlockSync`), persisted in localStorage.
 *   - On startup we also honour the SW's own sentinel cache
 *     (`coach-block-v1`, see public/sw-license-block.js) so devices blocked
 *     before this flag existed stay blocked.
 *   - While persisted-blocked the license gate stays closed until a server
 *     answer arrives: `/license/refresh` ok (`markServerConfirmedValid()`) or
 *     `/billing/state` with `effective.valid` — which then posts
 *     `LICENSE_UNBLOCKED` and clears the flag.
 *
 * All storage access is wrapped: private mode / disabled storage must not
 * crash the app (it then falls back to the in-memory value).
 */

const FLAG_KEY = "coach.licenseBlocked";
const SW_BLOCK_CACHE = "coach-block-v1";

export interface LicenseBlockFlag {
  /** A block was recorded on this device and not yet lifted by the server. */
  persistedBlocked: boolean;
  /** The server confirmed a valid license during this session. */
  serverConfirmed: boolean;
}

function readStoredFlag(): boolean {
  try {
    return localStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

let snapshot: LicenseBlockFlag = { persistedBlocked: readStoredFlag(), serverConfirmed: false };
const listeners = new Set<() => void>();
/** Set once any mark* call ran — a late sentinel probe must not override it. */
let decided = false;

function update(next: Partial<LicenseBlockFlag>): void {
  const merged = { ...snapshot, ...next };
  if (
    merged.persistedBlocked === snapshot.persistedBlocked &&
    merged.serverConfirmed === snapshot.serverConfirmed
  ) {
    return;
  }
  snapshot = merged;
  for (const l of listeners) l();
}

// Devices blocked by an older app version only have the SW sentinel cache.
// Window-side CacheStorage exists on iPadOS 17 Safari (secure context).
try {
  if (typeof caches !== "undefined") {
    void caches
      .has(SW_BLOCK_CACHE)
      .then((has) => {
        // Only adopt the SW block if nothing has decided since startup.
        if (has && !decided) {
          try {
            localStorage.setItem(FLAG_KEY, "1");
          } catch {
            /* in-memory only */
          }
          update({ persistedBlocked: true });
        }
      })
      .catch(() => {
        /* CacheStorage unavailable — rely on the localStorage flag */
      });
  }
} catch {
  /* ignore */
}

export function markLicenseBlocked(): void {
  decided = true;
  try {
    localStorage.setItem(FLAG_KEY, "1");
  } catch {
    /* in-memory only */
  }
  update({ persistedBlocked: true, serverConfirmed: false });
}

export function markLicenseUnblocked(): void {
  decided = true;
  try {
    localStorage.removeItem(FLAG_KEY);
  } catch {
    /* ignore */
  }
  update({ persistedBlocked: false });
}

/** `/license/refresh` minted a fresh token — the server says the license is valid. */
export function markServerConfirmedValid(): void {
  decided = true;
  update({ serverConfirmed: true });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): LicenseBlockFlag {
  return snapshot;
}

export function useLicenseBlockFlag(): LicenseBlockFlag {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
